import type { CSSProperties } from 'react'
import { useI18n } from '../../i18n'
import { formatSignedDelta, type HomeProblemsBreakdown } from '../../lib/assetsMonitoring'
import entStyles from '../../styles/enterprise/EnterpriseOperationalUx.module.scss'

export type HomeMetricNavKey =
  | 'informationSystems'
  | 'assetsTotal'
  | 'assetsWithVulnerabilities'
  | 'openFindings'
  | 'critical'
  | 'high'
  | 'medium'
  | 'low'
  | 'problems'

type Props = {
  informationSystemsCount: number
  assetsCount: number
  assetsWithVulnerabilities?: number
  openFindings?: number
  problems: HomeProblemsBreakdown
  severity: { critical: number; high: number; medium: number; low: number; info?: number }
  assetsDelta?: number
  sinceLastScanDelta?: number | null
  monitoringRaw?: Record<string, unknown> | null
  onNavigateMetric?: (key: HomeMetricNavKey) => void
}

function MetricTile({
  label,
  value,
  valueStyle,
  metricKey,
  onNavigate,
}: {
  label: string
  value: number | string
  valueStyle?: CSSProperties
  metricKey: HomeMetricNavKey
  onNavigate?: (key: HomeMetricNavKey) => void
}) {
  const clickable = typeof onNavigate === 'function'
  const className = clickable
    ? `${entStyles.postureMetric} ${entStyles.postureMetricClickable}`
    : entStyles.postureMetric

  if (clickable) {
    return (
      <button
        type="button"
        className={className}
        onClick={() => onNavigate?.(metricKey)}
        aria-label={label}
        data-metric={metricKey}
      >
        <p className={entStyles.postureMetricLabel}>{label}</p>
        <p className={entStyles.postureMetricValue} style={valueStyle}>
          {value}
        </p>
      </button>
    )
  }

  return (
    <div className={className} data-metric={metricKey}>
      <p className={entStyles.postureMetricLabel}>{label}</p>
      <p className={entStyles.postureMetricValue} style={valueStyle}>
        {value}
      </p>
    </div>
  )
}

export default function HomeOrganizationAssetsPanel({
  informationSystemsCount,
  assetsCount,
  assetsWithVulnerabilities = 0,
  openFindings = 0,
  problems,
  severity,
  assetsDelta = 0,
  sinceLastScanDelta = null,
  monitoringRaw,
  onNavigateMetric,
}: Props) {
  const { t } = useI18n()
  const am = monitoringRaw as Record<string, any> | null | undefined
  const delta =
    sinceLastScanDelta != null
      ? sinceLastScanDelta
      : Number(am?.sinceLastScan?.deltaUniqueFindings ?? assetsDelta ?? 0)

  // Cards and summary sentence share the same DTO fields — never recompute independently
  const uniqueOpen = openFindings
  const summaryVuln = uniqueOpen
  const summaryRisks = Number(problems.risks || 0)
  const summaryControls = Number(problems.controls || 0)
  const sevSum =
    Number(severity.critical || 0) +
    Number(severity.high || 0) +
    Number(severity.medium || 0) +
    Number(severity.low || 0)

  return (
    <section
      className={`${entStyles.layer} ${entStyles.onDarkSurface}`}
      data-testid="home-organization-assets"
    >
      <p className={entStyles.layerLabel}>
        {t('home.organizationAssets.layerLabel', 'Portfolio exposure')}
      </p>
      <div className={entStyles.posturePanel}>
        <h2 className={entStyles.postureTitle}>
          {t('home.organizationAssets.title', 'Organization asset state')}
        </h2>
        <p className={entStyles.postureSubtitle}>
          {t(
            'home.organizationAssets.subtitle',
            'Current unique findings across information systems and assets. Historical scanner rows are not included.',
          )}
        </p>

        <div className={entStyles.postureGrid}>
          <MetricTile
            metricKey="informationSystems"
            label={t('home.metrics.informationSystems', 'Information systems')}
            value={informationSystemsCount}
            onNavigate={onNavigateMetric}
          />
          <MetricTile
            metricKey="assetsTotal"
            label={t('labels.assetsTotal', 'Assets')}
            value={assetsCount}
            onNavigate={onNavigateMetric}
          />
          <MetricTile
            metricKey="assetsWithVulnerabilities"
            label={t('reports.metrics.assetsWithVulns', 'Assets with vulnerabilities')}
            value={assetsWithVulnerabilities}
            onNavigate={onNavigateMetric}
          />
          <MetricTile
            metricKey="openFindings"
            label={t('complianceCenterUi.openFindings', 'Open findings')}
            value={uniqueOpen}
            onNavigate={onNavigateMetric}
          />
          <MetricTile
            metricKey="problems"
            label={t('labels.problems', 'Problems')}
            value={problems.total}
            valueStyle={{ color: '#f87171' }}
            onNavigate={onNavigateMetric}
          />
          <MetricTile
            metricKey="critical"
            label={t('labels.severityCritical', 'Critical')}
            value={severity.critical}
            onNavigate={onNavigateMetric}
          />
          <MetricTile
            metricKey="high"
            label={t('labels.severityHigh', 'High')}
            value={severity.high}
            onNavigate={onNavigateMetric}
          />
          <MetricTile
            metricKey="medium"
            label={t('labels.severityMedium', 'Medium')}
            value={severity.medium}
            onNavigate={onNavigateMetric}
          />
          <MetricTile
            metricKey="low"
            label={t('labels.severityLow', 'Low')}
            value={severity.low}
            onNavigate={onNavigateMetric}
          />
        </div>

        <p className={entStyles.postureStory} data-testid="home-problems-breakdown">
          {t(
            'home.metrics.problemsBreakdownFailed',
            '{vuln} vulnerabilities · {risks} risks · {failed} failed controls',
            {
              vuln: String(summaryVuln),
              risks: String(summaryRisks),
              failed: String(summaryControls),
            },
          )}
        </p>
        <p className={entStyles.scopeLine} style={{ marginTop: 8 }}>
          {t('home.metrics.hoverDeltaSinceScan', '{delta} since last scan', {
            delta: formatSignedDelta(delta),
          })}
          {' · '}
          {t(
            'home.metrics.openFindingsEqualsCards',
            'Open findings = Critical + High + Medium + Low (+ Info when present)',
          )}
          {severity.info != null && Number(severity.info) > 0
            ? ` · ${t('labels.severityInfo', 'Info')}: ${severity.info}`
            : sevSum !== uniqueOpen && uniqueOpen > sevSum
              ? ` · ${t('home.metrics.infoExcludedNote', 'Info findings are included in Open findings but not shown as a card')}`
              : ''}
        </p>
      </div>
    </section>
  )
}
